import React, { useState, useCallback } from 'react';
import {Modal, Typography} from '@ann/web-components';
import { ContentWrapper } from './DisableUsersModal.styled';
import { deactivateUser } from '../../../../services';
import { useToast } from '../../../../../../../components/toast';

const DisableUsersModal = ({isOpen = false, onClose, selectedUsers = [], onRefetchUsers}) => {
  const isMultipleUsers = selectedUsers.length > 1;
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleDisable = useCallback(async () => {
    if (selectedUsers.length === 0) {
      return;
    }

    setLoading(true);

    try {
      // Extraer todos los userUuids del array de selectedUsers
      // Programación defensiva: soportar ambos formatos (uuid y userUuid)
      const userUuids = selectedUsers
        .map(user => user.uuid || user.userUuid || user.id)
        .filter(Boolean);

      if (userUuids.length === 0) {
        toast.error({
          message: 'No valid user UUIDs found',
          canClose: true,
        });
        return;
      }

      // Llamar al servicio con el array de userUuids (el servicio maneja Promise.all internamente)
      await deactivateUser(userUuids);

      // Mostrar mensaje de éxito
      const message = isMultipleUsers
        ? `${userUuids.length} user${userUuids.length > 1 ? 's' : ''} deactivated successfully.`
        : 'User deactivated successfully.';
      
      toast.success({
        message,
        canClose: true,
      });

      // Recargar la lista de usuarios después de desactivar exitosamente
      if (onRefetchUsers) {
        onRefetchUsers();
      }

      // Cerrar el modal si fue exitoso
      if (onClose) {
        onClose();
      }
    } catch (error) {
      // Extraer mensaje de error del response del endpoint
      let errorMessage = 'Failed to deactivate user(s).';
      
      if (error?.response?.data) {
        const errorData = error.response.data;
        // Intentar extraer el mensaje de diferentes formatos posibles
        if (errorData.msgCode && Array.isArray(errorData.msgCode) && errorData.msgCode.length > 0) {
          errorMessage = errorData.msgCode[0].description || errorData.msgCode[0].code || errorMessage;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error({
        message: errorMessage,
        canClose: true,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedUsers, isMultipleUsers, onClose, toast, onRefetchUsers]);

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} size="medium" onClose={handleCancel}>
      <Modal.Header/>
      <Modal.Body>
        <ContentWrapper>
          <Typography category="title" size="medium" weight="bold" >
            {isMultipleUsers ? `Disable ${selectedUsers.length} users?` : 'Disable this user?'}
          </Typography>
          <Typography category="body" size="medium" weight="regular" color="body">
            {isMultipleUsers
              ? (
                <span>
                  They’ll lose access to the workspace and tools until you re-enable them.<br/>
                  Their data will stay safe.
                </span>
              )
              : (
                <span>
                  They’ll lose access to the workspace and can’t sign in until you re-enable them.<br/>
                  Their data will stay safe.
                </span>
              )
            }
          </Typography>
        </ContentWrapper>
      </Modal.Body>
      <Modal.Actions align="center">
        <Modal.Button
          label="Cancel"
          onClick={handleCancel}
          type="secondary"
        />
        <Modal.Button
          label={isMultipleUsers ? "Disable users" : "Disable user"}
          onClick={handleDisable}
          disabled={loading || selectedUsers.length === 0}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default DisableUsersModal;